import 'regenerator-runtime/runtime';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { getSasToken } from '../actions';
import { publicShareApimCall, apimCall } from '../helpers/api';
import { getGuidsFromServer, getQuerystringParameters } from '../helpers/utility';
import { FieldTypes } from '../helpers/constants';
import { getCurrentPosition } from '../helpers/LocationUtility';
import { showBanner, showConfirm } from '../helpers/MessageUtility';
import { formatAsShortDate, convertFieldFormatToMoment, getDateTimeFormat } from '../helpers/DateTimeUtility';
import { HasRecoveredForm, GetRecoveredForm, ClearRecoveredForm } from './recovery';
import { analyticsTrack } from '../helpers/AnalyticsUtility';
import { RootState } from '../gf-types';
import { GoFormzServices } from '../AppSettings';
import { FieldValueUpdate, FormEditorState, FormEditorView, FormulaValueUpdate, ValidationResultsUpdate, SavedPublicForm } from './formeditor-types';
import { getMissingRequiredFields, MissingRequiredField } from './requiredFields';
import { DataLayoutPosition, FieldPosition } from '../types/template-types';
import { Form, FormTag, PublicForm, PublicFormDispatch } from '../types/form-types';
import { NavigateFunction } from 'react-router-dom';
import { BlockBlobClient } from '@azure/storage-blob';
import { ErrorType } from '../templateeditor/components/FieldValidations/constants';
import { getFeature } from '../helpers/PermissionUtility';
import { getAllInvalidFields } from './components/FieldValidations/selectors';
import { Features } from '../helpers/PermissionUtility';

export const FORM_EDITOR_INIT = 'FORM_EDITOR_INIT';
export const FORM_GET_FORM_REQUEST = 'FORM_GET_FORM_REQUEST';
export const FORM_GET_FORM_SUCCESS = 'FORM_GET_FORM_SUCCESS';
export const FORM_GET_FORM_FAILURE = 'FORM_GET_FORM_FAILURE';
export const FORM_SAVE_REQUEST = 'FORM_SAVE_REQUEST';
export const FORM_SAVE_SUCCESS = 'FORM_SAVE_SUCCESS';
export const FORM_SAVE_FAILURE = 'FORM_SAVE_FAILURE';
export const FORM_DUPLICATE_REQUEST = 'FORM_DUPLICATE_REQUEST';
export const FORM_DUPLICATE_SUCCESS = 'FORM_DUPLICATE_SUCCESS';
export const FORM_DUPLICATE_FAILURE = 'FORM_DUPLICATE_FAILURE';
export const FORM_TRANSFER_REQUEST = 'FORM_TRANSFER_REQUEST';
export const FORM_TRANSFER_SUCCESS = 'FORM_TRANSFER_SUCCESS';
export const FORM_TRANSFER_FAILURE = 'FORM_TRANSFER_FAILURE';
export const FORM_GET_TEMPLATE_REQUEST = 'FORM_GET_TEMPLATE_REQUEST';
export const FORM_GET_TEMPLATE_SUCCESS = 'FORM_GET_TEMPLATE_SUCCESS';
export const FORM_GET_TEMPLATE_FAILURE = 'FORM_GET_TEMPLATE_FAILURE';
export const FORM_GET_PUBLICSHARE_REQUEST = 'FORM_GET_PUBLICSHARE_REQUEST';
export const FORM_GET_PUBLICSHARE_FORM_SUCCESS = 'FORM_GET_PUBLICSHARE_FORM_SUCCESS';
export const FORM_GET_PUBLICSHARE_FILE_URLS_SUCCESS = 'FORM_GET_PUBLICSHARE_FILE_URLS_SUCCESS';
export const FORM_GET_PUBLICSHARE_TEMPLATE_SUCCESS = 'FORM_GET_PUBLICSHARE_TEMPLATE_SUCCESS';
export const FORM_GET_PUBLICSHARE_FAILURE = 'FORM_GET_PUBLICSHARE_FAILURE';
export const FORM_GET_PUBLICSHARE_IMAGE_URL_REQUEST = 'FORM_GET_PUBLICSHARE_IMAGE_URL_REQUEST';
export const FORM_GET_PUBLICSHARE_IMAGE_URL_SUCCESS = 'FORM_GET_PUBLICSHARE_IMAGE_URL_SUCCESS';
export const FORM_GET_PUBLICSHARE_IMAGE_URL_FAILURE = 'FORM_GET_PUBLICSHARE_IMAGE_URL_FAILURE';
export const FORM_UPDATE_FIELD_VALUES = 'FORM_UPDATE_FIELD_VALUES';
export const FORM_UPDATE_FORMULA_VALUES = 'FORM_UPDATE_FORMULA_VALUES';
export const FORM_UPDATE_VALIDATION_FORMULA_RESULTS = 'FORM_UPDATE_VALIDATION_FORMULA_RESULTS';
export const FORM_UPDATE_NAME = 'FORM_UPDATE_NAME';
export const FORM_UPDATE_TAGS = 'FORM_UPDATE_TAGS';
export const FORM_UPDATE_TEMPLATE_VERSION = 'FORM_UPDATE_TEMPLATE_VERSION';
export const FORM_CLOSE = 'FORM_CLOSE';
export const FORM_COMPLETE = 'FORM_COMPLETE';
export const FORM_COMPLETE_FIELDS_REQUIRED = 'FORM_COMPLETE_FIELDS_REQUIRED';
export const FORM_INVALID_FIELD_VALUES = 'FORM_INVALID_FIELD_VALUES';
export const FORM_COMPLETE_REQUEST = 'FORM_COMPLETE_REQUEST';
export const FORM_COMPLETE_SUCCESS = 'FORM_COMPLETE_SUCCESS';
export const FORM_CLOSE_ERROR = 'FORM_CLOSE_ERROR';
export const FORM_REOPEN_REQUEST = 'FORM_REOPEN_REQUEST';
export const FORM_REOPEN_SUCCESS = 'FORM_REOPEN_SUCCESS';
export const FORM_REOPEN_OPEN_WARNING = 'FORM_REOPEN_OPEN_WARNING';
export const FORM_REOPEN_CLOSE_WARNING = 'FORM_REOPEN_CLOSE_WARNING';
export const FORM_REOPEN_FAILURE = 'FORM_REOPEN_FAILURE';
export const FORMS_DELETE_REQUEST = 'FORMS_DELETE_REQUEST';
export const FORMS_DELETE_SUCCESS = 'FORMS_DELETE_SUCCESS';
export const FORMS_DELETE_FAILURE = 'FORMS_DELETE_FAILURE';
export const FORM_METADATA_REQUEST = 'FORM_METADATA_REQUEST';
export const FORM_METADATA_SUCCESS = 'FORM_METADATA_SUCCESS';
export const FORM_METADATA_FAILURE = 'FORM_METADATA_FAILURE';
export const FORM_CLOSE_OUTDATED = 'FORM_CLOSE_OUTDATED';
export const FORM_RECOVER = 'FORM_RECOVER';
export const UPDATE_SESSION_SIGNATURE = 'UPDATE_SESSION_SIGNATURE';
export const FORM_SET_HIGHLIGHT = 'FORM_SET_HIGHLIGHT';
export const FORM_RESET_HIGHLIGHT = 'FORM_RESET_HIGHLIGHT';
export const FORM_SET_VIEW = 'FORM_SET_VIEW';
export const UPDATE_SAVED_PUBLIC_FORM = 'UPDATE_SAVED_PUBLIC_FORM';
export const UPDATE_SAVED_PUBLIC_FORM_BLOB = 'UPDATE_SAVED_PUBLIC_FORM_BLOB';
export const PUBLIC_FORM_SAVE_REQUEST = 'PUBLIC_FORM_SAVE_REQUEST';
export const PUBLIC_FORM_SAVE_SUCCESS = 'PUBLIC_FORM_SAVE_SUCCESS';
export const PUBLIC_FORM_SAVE_FAILURE = 'PUBLIC_FORM_SAVE_FAILURE';
export const FORM_UPDATE_FIELD_HIGHLIGHT = 'FORM_UPDATE_FIELD_HIGHLIGHT';

export const setHighlight = (fieldPosition: FieldPosition, dataLayoutPosition: DataLayoutPosition) => (dispatch) => {
  dispatch({
    type: FORM_SET_HIGHLIGHT,
    payload: {
      highlight: {
        fieldPositionId: fieldPosition?.id,
        dataLayoutPositionId: dataLayoutPosition?.id,
        fieldId: fieldPosition?.fieldId ?? dataLayoutPosition?.fieldId
      }
    }
  });
};

export const setFormEditorView = (view: FormEditorView) => (dispatch) => {
  dispatch({
    type: FORM_SET_VIEW,
    payload: {
      view
    }
  });
};

function setDateTimeValues(fieldValueUpdates: FieldValueUpdate[]) {
  const result: FieldValueUpdate[] = [];
  for (const fieldValueUpdate of fieldValueUpdates) {
    // Set any missing dateTimeValue or timeValue properties
    if (fieldValueUpdate.fieldType === FieldTypes.DateTime && !fieldValueUpdate.dateTimeValue) {
      fieldValueUpdate.dateTimeValue = fieldValueUpdate.value
        ? moment(fieldValueUpdate.value, convertFieldFormatToMoment(fieldValueUpdate.dateTimeFormat)).toISOString()
        : null;
    } else if (fieldValueUpdate.fieldType === FieldTypes.Date && !fieldValueUpdate.dateTimeValue) {
      fieldValueUpdate.dateTimeValue = fieldValueUpdate.value
        ? moment(fieldValueUpdate.value, convertFieldFormatToMoment(fieldValueUpdate.dateTimeFormat)).toISOString()
        : null;
    } else if (fieldValueUpdate.fieldType === FieldTypes.Time && !fieldValueUpdate.timeValue) {
      fieldValueUpdate.timeValue = fieldValueUpdate.value
        ? moment(fieldValueUpdate.value, convertFieldFormatToMoment(fieldValueUpdate.dateTimeFormat)).toISOString()
        : null;
    }
    result.push(fieldValueUpdate);
  }
  return result;
}

export const updateFieldValues = (list: FieldValueUpdate[]) => async (dispatch, getState: () => RootState) => {
  if (!list || list.length === 0) {
    return;
  }
  const state = getState();
  const values = setDateTimeValues(list);

  dispatch({
    type: FORM_UPDATE_FIELD_VALUES,
    payload: {
      list: values,
      userFullName: state.profile.user.name,
      userEmail: state.profile.user.email,
      dateTimeFormat: getDateTimeFormat(),
      isUnsaved: true,
      fieldValidationsFF: getFeature(Features.FieldValidations)
    }
  });

  const fieldsRequiringLocation = values.filter((fv): boolean => {
    return fv.value && (fv.fieldType === FieldTypes.Signature || fv.fieldType === FieldTypes.Image);
  });

  if (fieldsRequiringLocation.length > 0) {
    const location = await getCurrentPosition({
      timeout: 10000, // 10 seconds
      maximumAge: 60 * 60000 // 60 minutes
    });
    if (location && location.coords) {
      const formValues = [];
      const coordinates = {
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        accuracy: location.coords.accuracy
      };
      fieldsRequiringLocation.forEach((fv) => {
        formValues.push({ coordinates, ...fv });
      });

      dispatch({
        type: FORM_UPDATE_FIELD_VALUES,
        payload: {
          list: formValues,
          userFullName: state.profile.user.name,
          userEmail: state.profile.user.email,
          dateTimeFormat: getDateTimeFormat(),
          isUnsaved: true,
          fieldValidationsFF: getFeature(Features.FieldValidations)
        }
      });
    }
  }
};

export const updateFormulaValues = (list: FormulaValueUpdate[]) => ({
  type: FORM_UPDATE_FORMULA_VALUES,
  payload: {
    list
  }
});

export const updateValidationResults = (list: ValidationResultsUpdate[]) => ({
  type: FORM_UPDATE_VALIDATION_FORMULA_RESULTS,
  payload: {
    list
  }
});

export const formEditorUpdateName = (name: string, manualNameOverride: boolean, autoGenerated: boolean) => ({
  type: FORM_UPDATE_NAME,
  payload: {
    name,
    manualNameOverride,
    autoGenerated,
    dateTimeFormat: getDateTimeFormat()
  }
});

export const formEditorUpdateTags = (tags) => ({
  type: FORM_UPDATE_TAGS,
  payload: { tags }
});

export const updateFieldHighlight = (fields: FieldValueUpdate[]) => ({
  type: FORM_UPDATE_FIELD_HIGHLIGHT,
  payload: {
    fields
  }
});

export const getFormAndTemplate = (formId: string, versionId?) => async (dispatch) => {
  dispatch({
    type: FORM_GET_FORM_REQUEST,
    payload: {
      isFetching: true,
      error: null
    }
  });

  try {
    const url = versionId ? `/formAndTemplate/${formId}/versions/${versionId}` : `/formAndTemplate/${formId}`;
    const response = await apimCall(GoFormzServices.TemplateService, url);
    dispatch({
      type: FORM_GET_FORM_SUCCESS,
      payload: {
        error: null,
        isFetching: false,
        transferred: false,
        form: response.data.form,
        template: response.data.template,
        itemCollections: response.data.itemCollections,
        templateLoaded: true,
        formLoaded: true,
        userMap: response.data.userMap,
        versionId
      }
    });
    return true;
  } catch (error) {
    console.error(error);
    dispatch({
      type: FORM_GET_FORM_FAILURE,
      payload: {
        isFetching: false,
        notFound: true
      }
    });
    return false;
  }
};

const reloadPublicShareFileUrls = (sessionId: string) => async (dispatch) => {
  const response = await publicShareApimCall(`/forms/${sessionId}`);
  dispatch({
    type: FORM_GET_PUBLICSHARE_FILE_URLS_SUCCESS,
    payload: {
      fileUrls: response.data.fileUrls
    }
  });
  //continue refreshing file urls every 90 minutes
  setTimeout(() => reloadPublicShareFileUrls(sessionId)(dispatch), 5400000);
};

export const getSharedFormAndTemplate = (sessionId: string, publicFormData?: PublicForm) => async (dispatch) => {
  // todo - make sure session is truthy
  dispatch({
    type: FORM_GET_PUBLICSHARE_REQUEST,
    payload: {
      isFetching: true,
      error: null
    }
  });

  try {
    let response = null;
    // if we have a saved public form, pass this data to the public form service
    // this is needed so we can get and set the file urls for the form values for blob access
    if (publicFormData) {
      response = await publicShareApimCall(`/forms/${sessionId}/savedPublicForm`, 'POST', publicFormData);
    } else {
      response = await publicShareApimCall(`/forms/${sessionId}`);
    }
    dispatch({
      type: response.data.form ? FORM_GET_PUBLICSHARE_FORM_SUCCESS : FORM_GET_PUBLICSHARE_TEMPLATE_SUCCESS,
      payload: {
        error: null,
        isFetching: false,
        transferred: false,
        form: response.data.form,
        template: response.data.template,
        itemCollections: response.data.itemCollections,
        policyInfo: response.data.policyInfo,
        fileUrls: response.data.fileUrls,
        templateLoaded: true,
        formLoaded: !!response.data.form,
        isNewForm: !response.data.form,
        userMap: {},
        sessionId: response.data.sessionId,
        dateFormat: response.data.dateFormat,
        features: response.data.features,
        savePublicFormEnabled: response.data.policyInfo?.savePublicFormEnabled
      }
    });
    // set timer to reload the page background urls after 90 minutes
    setTimeout(() => reloadPublicShareFileUrls(sessionId)(dispatch), 5400000);
  } catch (error) {
    dispatch({
      type: FORM_GET_PUBLICSHARE_FAILURE,
      payload: {
        error: { title: 'Error', message: error?.response?.data ?? 'Server Error' },
        isFetching: false
      }
    });
  }
};

export const getSharedFormImageUrl = (fileId: string) => async (dispatch, getState: () => RootState) => {
  dispatch({
    type: FORM_GET_PUBLICSHARE_IMAGE_URL_REQUEST,
    isFetching: true,
    error: null
  });

  try {
    const sessionId = getState().formeditor?.sessionId;
    const response = await publicShareApimCall('/files/sasUrls', 'POST', { ids: [fileId], sessionId });
    dispatch({
      type: FORM_GET_PUBLICSHARE_IMAGE_URL_SUCCESS,
      payload: {
        isFetching: false,
        fileUrls: response?.data?.data
      }
    });
  } catch (error) {
    dispatch({
      type: FORM_GET_PUBLICSHARE_IMAGE_URL_FAILURE,
      payload: {
        isFetching: false,
        error: { title: 'Error', message: error?.response?.data ?? 'Server Error' }
      }
    });
  }
};

export const submitSharedForm = () => async (dispatch, getState: () => RootState) => {
  const state: RootState = getState();
  const form = { ...state.formeditor.form } as PublicForm;
  const savedPublicForm = { ...state.formeditor.savedPublicForm } as SavedPublicForm;

  form.sessionId = state.formeditor.sessionId;
  form.trackingId = localStorage.getItem('trackingId');

  if (!localStorage.getItem('trackingId')) {
    localStorage.setItem('trackingId', uuidv4());
  }

  const missingRequiredFields = getMissingRequiredFields(state.formeditor);
  if (missingRequiredFields.length > 0) {
    dispatchMissingRequiredFields(missingRequiredFields, dispatch);
  } else {
    showConfirm(
      {
        title: 'Confirm',
        msg: `Are you sure you are ready to submit the form?`,
        className: 'confirm-submit-public-form'
      },
      async (result) => {
        if (result) {
          dispatch({
            type: FORM_SAVE_REQUEST,
            payload: {
              isNewForm: false,
              isSaving: true,
              isFetching: false,
              error: null
            }
          });

          try {
            const response = await publicShareApimCall('/forms', 'POST', form);
            if (response && savedPublicForm?.saveId) {
              await publicShareApimCall(`/savedpublicform/submit/${savedPublicForm.saveId}`, 'POST', null, null, null, savedPublicForm.saveId);
            }
            dispatch({
              type: FORM_SAVE_SUCCESS,
              payload: {
                isSaving: false,
                isDirty: false,
                lastSaveDate: new Date(),
                isUnsaved: false,
                publicShareSubmitted: true,
                pdfPath: response?.data?.location
              }
            });
            // TODO change to post submit view
            dispatch({
              type: FORM_CLOSE
            });
          } catch (error) {
            dispatch({
              type: FORM_SAVE_FAILURE,
              payload: {
                isSaving: false,
                error: { title: 'Error', message: error?.response?.data ?? 'Server Error' },
                isFetching: false
              }
            });
          }
        }
      }
    );
  }
};

export const transferForm =
  (recipient: string, transferType: 0 | 1, redirectOnTransfer = true, navigate?: NavigateFunction, comments?: string) =>
  (dispatch, getState: () => RootState) => {
    const state: RootState = getState();
    const formId = state.formeditor.form.id;
    const form = { ...state.formeditor.form };

    if (!form.transferHistory) {
      form.transferHistory = [];
    }

    const newItem = {
      sourceId: form.userId ? form.userId : form.groupId,
      destId: recipient,
      date: moment().toISOString(),
      type: transferType,
      comments
    };
    form.transferHistory = [...form.transferHistory, newItem];

    if (transferType === 0) {
      form.userId = recipient;
      form.groupId = null;
    } else if (transferType === 1) {
      form.groupId = recipient;
      form.userId = null;
    }

    dispatch({
      type: FORM_TRANSFER_REQUEST,
      payload: {
        isFetching: true,
        error: null,
        form: form
      }
    });

    return apimCall(GoFormzServices.TemplateService, `/forms/${formId}`, 'PUT', form)
      .then(() => {
        dispatch({
          type: FORM_TRANSFER_SUCCESS,
          payload: {
            isFetching: false
          }
        });
        analyticsTrack('Form Transferred', {
          templateName: state.formeditor.template?.name
        });
        if (redirectOnTransfer) {
          showBanner('Form transferred');
          navigate('/forms');
        } else {
          getFormAndTemplate(formId)(dispatch);
        }
      })
      .catch((error) =>
        dispatch({
          type: FORM_TRANSFER_FAILURE,
          payload: {
            error: { title: 'Error', message: error?.response?.data ?? 'Server Error' },
            isFetching: false
          }
        })
      );
  };

let saveFormPromise = null;

export const saveForm =
  (saveBannerText = 'Form saved', forceSave = false, updateHistory = true) =>
  (dispatch, getState: () => RootState) => {
    const state: RootState = getState();
    const isSaving = state.formeditor.isSaving;
    if (!isSaving || saveFormPromise === null) {
      saveFormPromise = innerSaveForm(dispatch, getState, saveBannerText, forceSave, updateHistory);
    }
    return saveFormPromise.then(() => {
      saveFormPromise = null;
    });
  };

async function innerSaveForm(dispatch, getState: () => RootState, saveBannerText, forceSave, updateHistory = true) {
  const state: RootState = getState();
  const formId = state.formeditor.form?.id;

  // Clone the form so it can be manipulated before submitting
  // For autotags, remove the operation - the server will handle applying the autotags
  const form = JSON.parse(JSON.stringify(state.formeditor.form)) as Form;
  form?.tags?.forEach((x: FormTag) => {
    if (x.isAutoTag) {
      x.operation = null;
    }
  });

  if (!forceSave && !state.formeditor.isNewForm) {
    const isOutdated = await checkOutdated('Save')(dispatch, getState);
    if (isOutdated) {
      return;
    }
  }

  dispatch({
    type: FORM_SAVE_REQUEST,
    payload: {
      isNewForm: false,
      isSaving: true,
      isFetching: false,
      error: null
    }
  });

  return apimCall(GoFormzServices.TemplateService, `/forms/${formId}`, 'PUT', form)
    .then((response) => {
      dispatch({
        type: FORM_SAVE_SUCCESS,
        payload: {
          isSaving: false,
          isDirty: false,
          lastSaveDate: new Date(),
          isUnsaved: false,
          lastUpdatedDate: response.data.lastUpdatedDate,
          isNewForm: false
        }
      });
      showBanner(saveBannerText);
      ClearRecoveredForm(formId);
    })
    .then(() => {
      if (updateHistory) {
        history.replaceState(null, null, `/forms/${formId}`);
      }
    })
    .catch(() =>
      dispatch({
        type: FORM_SAVE_FAILURE,
        payload: {
          isSaving: false,
          error: { title: 'Error', message: 'There was an error saving your form. If it persists please contact support.' },
          isFetching: false
        }
      })
    );
}

export const duplicateForm = (navigate: NavigateFunction) => (dispatch, getState: () => RootState) => {
  const state = getState();
  const formId = state.formeditor.form?.id;
  const requestBody = { formId };

  dispatch({
    type: FORM_DUPLICATE_REQUEST,
    payload: {
      isFetching: true,
      error: null
    }
  });

  apimCall(GoFormzServices.TemplateService, `/forms/copy/`, 'POST', requestBody)
    .then(async (response) => {
      const id = response.data;
      dispatch({
        type: FORM_DUPLICATE_SUCCESS,
        payload: {
          isFetching: false
        }
      });
      analyticsTrack('Form Copied', {
        templateName: getState().formeditor.template?.name
      });
      showBanner('Duplication successful - you’re now editing a copy of your Form');
      navigate(`/forms/${id}`);
    })
    .catch((error) => {
      dispatch({
        type: FORM_DUPLICATE_FAILURE,
        payload: {
          error: { title: 'Error duplicating form', message: error?.response?.data ?? 'Server Error' },
          isFetching: false
        }
      });
    });
};

function dispatchMissingRequiredFields(missingRequiredFields: MissingRequiredField[], dispatch) {
  dispatch({
    type: FORM_COMPLETE_FIELDS_REQUIRED,
    payload: { missingRequiredFieldIds: missingRequiredFields.map((x) => x.id) }
  });
}

export const completeForm =
  (view: FormEditorView, previewMode: boolean, forceComplete = false) =>
  async (dispatch, getState: () => RootState) => {
    const state: RootState = getState();
    const missingRequiredFields = getMissingRequiredFields(state.formeditor);
    const invalidFieldValues = getAllInvalidFields(state.formeditor);
    const hasErrorFieldValues = invalidFieldValues?.filter((x) => x.errorSettings.errorType === ErrorType.Error).length > 0;

    if (missingRequiredFields?.length > 0 || hasErrorFieldValues) {
      if (hasErrorFieldValues) {
        dispatch({
          type: FORM_INVALID_FIELD_VALUES,
          payload: { invalidFieldValues }
        });
      }

      if (missingRequiredFields?.length > 0) {
        dispatchMissingRequiredFields(missingRequiredFields, dispatch);
      }
    } else if (previewMode) {
      showBanner('Test complete successful');
    } else {
      if (!forceComplete) {
        const isOutdated = await checkOutdated('Complete')(dispatch, getState);
        if (isOutdated) {
          return;
        }
      }

      dispatch({
        type: FORM_COMPLETE_REQUEST,
        payload: {
          completeInProgress: true
        }
      });
      const location = await getCurrentPosition({
        timeout: 6000, // 6 seconds
        maximumAge: 60 * 60000 // 60 minutes
      });
      dispatch({
        type: FORM_COMPLETE,
        payload: {
          location:
            location && location.coords
              ? {
                  latitude: location.coords.latitude,
                  longitude: location.coords.longitude
                }
              : null
        }
      });
      await saveForm('Form completed', forceComplete)(dispatch, getState);
      dispatch({
        type: FORM_COMPLETE_SUCCESS,
        payload: {
          completeInProgress: false
        }
      });
      analyticsTrack('Form Completed', {
        templateName: state.formeditor.template?.name,
        view
      });
    }
  };

export const completeHipaaForm = () => async (dispatch, getState: () => RootState) => {
  // todo - clean this up
  const state: RootState = getState();
  const missingRequiredFields = getMissingRequiredFields(state.formeditor);

  if (missingRequiredFields.length > 0) {
    dispatchMissingRequiredFields(missingRequiredFields, dispatch);

    return;
  }

  dispatch({
    type: FORM_COMPLETE_REQUEST,
    payload: {
      completeInProgress: true
    }
  });

  const currentPosition = await getCurrentPosition({
    timeout: 6000, // 6 seconds
    maximumAge: 60 * 60000 // 60 minutes
  });

  const location = currentPosition?.coords
    ? {
        latitude: currentPosition.coords.latitude,
        longitude: currentPosition.coords.longitude
      }
    : null;

  dispatch({
    type: FORM_COMPLETE,
    payload: { location }
  });

  await saveForm('Form completed', false, false)(dispatch, getState);

  dispatch({
    type: FORM_COMPLETE_SUCCESS,
    payload: { completeInProgress: false }
  });
};

export const reopenForm = (skipWarning) => async (dispatch, getState: () => RootState) => {
  const state: RootState = getState();
  const skipFormStatusChangeWarning = state.profile.settings.skipFormStatusChangeWarning;
  if (!skipFormStatusChangeWarning && !skipWarning) {
    dispatch({ type: FORM_REOPEN_OPEN_WARNING });
  } else {
    await innerReopenForm(dispatch, getState);
    analyticsTrack('Form Reopened', {
      templateName: state.formeditor.template?.name
    });
  }
};

async function innerReopenForm(dispatch, getState: () => RootState) {
  const state: RootState = getState();
  const formId = state.formeditor.form?.id;

  dispatch({
    type: FORM_REOPEN_REQUEST,
    payload: {
      isSaving: true,
      isFetching: false,
      error: null
    }
  });

  return apimCall(GoFormzServices.TemplateService, `/forms/${formId}/status`, 'POST', {
    status: 'draft'
  })
    .then(() => {
      dispatch({
        type: FORM_REOPEN_SUCCESS,
        payload: {
          isSaving: false,
          isDirty: false,
          lastSaveDate: new Date(),
          isUnsaved: false
        }
      });
      showBanner('Form reopened');
    })
    .catch(() =>
      dispatch({
        type: FORM_REOPEN_FAILURE,
        payload: {
          isSaving: false,
          error: { title: 'Error', message: 'There was an error reopening your form. If it persists please contact support.' },
          isFetching: false
        }
      })
    );
}

export const closeReopenFormWarning = () => ({
  type: FORM_REOPEN_CLOSE_WARNING
});

export const formEditorCloseError = () => ({
  type: FORM_CLOSE_ERROR,
  payload: {
    error: null
  }
});

export const formEditorCloseOutdated = () => ({
  type: FORM_CLOSE_OUTDATED,
  payload: {
    outdated: null
  }
});

export const getTemplate = (templateId, previewMode) => async (dispatch, getState: () => RootState) => {
  dispatch({
    type: FORM_GET_TEMPLATE_REQUEST,
    payload: {
      isFetching: true,
      error: null
    }
  });
  const url = previewMode ? `/templates/${templateId}/draft?includeItemCollections=true` : `/templates/${templateId}?includeItemCollections=true`;
  try {
    const response = await apimCall(GoFormzServices.TemplateService, url);
    const userMap = {};
    userMap[getState().profile.user.id] = getState().profile.user.fullName;

    dispatch({
      type: FORM_GET_TEMPLATE_SUCCESS,
      payload: {
        error: null,
        isFetching: false,
        template: response.data.template,
        templateLoaded: true,
        itemCollections: response.data.itemCollections,
        isNewForm: true,
        userMap
      },
      userId: getState().profile.user.id
    });
  } catch (error) {
    console.error(error.response.data);
    dispatch({
      type: FORM_GET_TEMPLATE_FAILURE,
      payload: {
        isFetching: false,
        notFound: true
      }
    });
  }
};

export const initFormEditor =
  (sharedAccessToken?: string, formId?: string, sessionId?: string, publicFormData?: PublicForm, queryParams?: Record<string, string>) =>
  async (dispatch, getState: () => RootState) => {
    // taking in qureyParams as an optional parameter to allow for loading a template preview form from the send a form v2 modal
    const params = queryParams ?? getQuerystringParameters(true);
    const previewMode = params.preview === 'true';
    const templateId = params.templateid;
    const isPublicForm = !!sharedAccessToken;

    dispatch({
      type: FORM_EDITOR_INIT,
      payload: {
        isFetching: !!(formId || templateId || sharedAccessToken),
        sharedAccessToken,
        previewMode,
        sessionId
      }
    });

    await getGuidsFromServer(10000);

    if (isPublicForm) {
      getSharedFormAndTemplate(sessionId, publicFormData)(dispatch);
    } else {
      getSasToken()(dispatch);

      if (formId) {
        // existing form
        const versionId = params.versionid;
        const success = await getFormAndTemplate(formId, versionId)(dispatch);

        if (success && !versionId) {
          const hasRecoveredForm = HasRecoveredForm(formId);
          if (hasRecoveredForm) {
            const recoveredForm = GetRecoveredForm(formId);

            showConfirm(
              {
                title: 'Do you want to recover your form?',
                // eslint-disable-next-line max-len
                msg: `Do you want to restore the changes made by ${recoveredForm.user.name} ${moment(recoveredForm.timeStamp).fromNow()}? 
       
              <b>Note</b>: <i>By choosing to restore this form, you may override changes saved by other users after the above user's session ended.</i>`
              },
              async (result) => {
                if (result) {
                  dispatch({
                    type: FORM_RECOVER,
                    payload: {
                      form: recoveredForm.form
                    }
                  });
                }

                // cleanup the recovered template
                ClearRecoveredForm(formId);
              }
            );
          }
        }
      } else if (templateId) {
        // new form
        await getTemplate(templateId, previewMode)(dispatch, getState);

        if (!previewMode) {
          analyticsTrack('New Form Started', {
            templateName: getState().formeditor.template?.name
          });
        }
      }
    }
  };

export const deleteForm = (id: string, navigate: NavigateFunction) => async (dispatch) => {
  dispatch({ type: FORMS_DELETE_REQUEST });
  try {
    await apimCall(GoFormzServices.TemplateService, '/forms', 'DELETE', [id]);

    dispatch({
      type: FORMS_DELETE_SUCCESS,
      payload: {
        id,
        isUnsaved: false
      }
    });
    navigate('/forms');
  } catch (err) {
    dispatch({ type: FORMS_DELETE_FAILURE });
  }
};

export const checkOutdated = (action) => async (dispatch, getState: () => RootState) => {
  const state: RootState = getState();
  const formId = state.formeditor.form?.id;
  const lastUpdatedDate = state.formeditor.form?.lastUpdatedDate;

  dispatch({ type: FORM_METADATA_REQUEST, payload: { isFetching: true } });
  try {
    const response = await apimCall(GoFormzServices.TemplateService, `/forms/${formId}/metadata`);
    let outdated = null;
    if (new Date(response.data.lastUpdatedDate) > new Date(lastUpdatedDate)) {
      outdated = {};
      const shortTimeFormat = state.profile.dateFormat.shortTime;
      const currentUser = state.profile.user.id;
      outdated.message = `${
        currentUser && currentUser === response.data.lastUpdatedBy.id ? 'You' : response.data.lastUpdatedBy.name ?? 'Unknown User'
      } saved a new version of this form on ${formatAsShortDate(response.data.lastUpdatedDate)} at ${moment
        .utc(response.data.lastUpdatedDate)
        .local()
        .format(shortTimeFormat as string)}.`;
      outdated.action = action;
    }

    dispatch({
      type: FORM_METADATA_SUCCESS,
      payload: {
        outdated,
        isFetching: false
      }
    });

    return outdated !== null;
  } catch (err) {
    dispatch({ type: FORM_METADATA_FAILURE, payload: { isFetching: false } });
    return false;
  }
};

export const updateTemplateVersion = (templateVersionId) => async (dispatch, getState: () => RootState) => {
  const form = getState().formeditor.form;

  dispatch({
    type: FORM_UPDATE_TEMPLATE_VERSION,
    payload: {
      templateVersionId
    }
  });

  await saveForm()(dispatch, getState);
  await getFormAndTemplate(form.id)(dispatch);
};

export const updateSessionSignature = (signature, fieldType) => (dispatch) => {
  dispatch({
    type: UPDATE_SESSION_SIGNATURE,
    payload: signature,
    fieldType
  });
};

export const savePublicForm = () => async (dispatch, getState: () => RootState) => {
  const state: RootState = getState();
  const formeditor = { ...state.formeditor } as FormEditorState;
  const formName = formeditor.form.name as string;
  const emailAddress = formeditor.savedPublicForm.emailAddress as string;
  const sharedAccessToken = formeditor.sharedAccessToken as string;

  if (!emailAddress || !formName || !sharedAccessToken) {
    dispatch({
      type: PUBLIC_FORM_SAVE_FAILURE,
      payload: {
        isPublicFormSaving: false,
        error: { title: 'Error', message: 'Missing required values to save.' }
      }
    });
    return;
  }
  dispatch({
    type: PUBLIC_FORM_SAVE_REQUEST,
    payload: {
      isPublicFormSaving: true,
      error: null
    }
  });

  try {
    const requestBody: { emailAddress: string; formName: string; token: string; saveId?: string } = {
      emailAddress: formeditor.savedPublicForm.emailAddress,
      formName: formeditor.form.name,
      token: formeditor.sharedAccessToken
    };

    if (formeditor.savedPublicForm.saveId) {
      requestBody.saveId = formeditor.savedPublicForm.saveId;
    }

    const response = await publicShareApimCall<SavedPublicForm>(`/savedpublicform/save`, 'POST', requestBody, null, formeditor.sharedAccessToken);
    if (response?.data) {
      dispatch(updateSavedPublicFormState(response.data));
      dispatch(setSavedPublicFormBlob());
      dispatch({
        type: PUBLIC_FORM_SAVE_SUCCESS,
        payload: {
          isPublicFormSaving: false,
          isDirty: false,
          isUnsaved: false
        }
      });
      showBanner('Progress saved successfully');
    }
  } catch (err) {
    dispatch({
      type: PUBLIC_FORM_SAVE_FAILURE,
      payload: {
        isPublicFormSaving: false,
        error: { title: 'Error', message: err?.response?.data ?? 'Server Error' }
      }
    });
  }
};

export const updateSavedPublicFormState = (savedPublicForm: SavedPublicForm) => (dispatch) => {
  dispatch({
    type: UPDATE_SAVED_PUBLIC_FORM,
    payload: savedPublicForm
  });
};

export function getSavedPublicFormBlob() {
  return async function (dispatch: PublicFormDispatch, getState: () => RootState) {
    try {
      const state: RootState = getState();
      const savedPublicForm = { ...state.formeditor.savedPublicForm } as SavedPublicForm;

      const blobClient = await getSavedPublicFormBlobClient(savedPublicForm.saveId);

      const json = await blobClient.download();
      const jsonBlob = await json.blobBody;
      const jsonText = await jsonBlob.text();
      const publicForm = JSON.parse(jsonText) as PublicForm;

      dispatch({
        type: UPDATE_SAVED_PUBLIC_FORM_BLOB,
        payload: publicForm
      });
    } catch (error) {
      console.error('Failed to get saved public form blob:', error);
    }
  };
}

export function setSavedPublicFormBlob() {
  return async function (dispatch: PublicFormDispatch, getState: () => RootState) {
    const state: RootState = getState();
    const savedPublicForm = { ...state.formeditor.savedPublicForm } as SavedPublicForm;
    const publicForm = { ...state.formeditor.form } as PublicForm;

    try {
      const publicFormJson = JSON.stringify(publicForm ?? {});
      const publicFormJsonBlobLength = new Blob([publicFormJson]).size;

      getSavedPublicFormBlobClient(savedPublicForm.saveId)
        .then((blobClient) => blobClient.upload(publicFormJson, publicFormJsonBlobLength))
        .catch((e) => console.log(e));
    } catch (err) {
      console.error(err);
    }

    dispatch({
      type: UPDATE_SAVED_PUBLIC_FORM_BLOB,
      payload: publicForm
    });
  };
}

async function getSavedPublicFormBlobClient(saveId: string) {
  const blobUrlResponse = await publicShareApimCall<string>(`/savedpublicform/sas/${saveId}`, 'GET', null, null, null, saveId);
  if (blobUrlResponse.status != 200) {
    console.error('Failed To Get SavedPublicForm BlobUrl', blobUrlResponse);
    return null;
  }

  const blobClient = new BlockBlobClient(blobUrlResponse.data);
  return blobClient;
}

export const resetHighlight = () => ({
  type: FORM_RESET_HIGHLIGHT
});
